import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme, highContrastTheme } from '../theme/theme';
import { ThemeContext } from './themeContext';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Check for saved theme preference or system preference
  const getInitialTheme = (): boolean => {
    // Check localStorage first
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme !== null) {
      return savedTheme === 'dark';
    }

    // Check if dark mode is enabled from environment
    if (import.meta.env.VITE_ENABLE_DARK_MODE === 'false') {
      return false;
    }

    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const getInitialHighContrast = (): boolean => {
    // Check localStorage for high contrast preference
    const savedHighContrast = localStorage.getItem('highContrast');
    return savedHighContrast === 'true';
  };

  const [isDarkMode, setIsDarkMode] = useState<boolean>(getInitialTheme);
  const [isHighContrastMode, setIsHighContrastMode] = useState<boolean>(getInitialHighContrast);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't set a preference and high contrast is not enabled
      if (localStorage.getItem('theme') === null && !isHighContrastMode) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isHighContrastMode]);

  // Save high contrast preference
  useEffect(() => {
    localStorage.setItem('highContrast', isHighContrastMode ? 'true' : 'false');
  }, [isHighContrastMode]);

  // Persist the theme only on an explicit user choice. Persisting the derived
  // initial value (e.g. the system preference) on mount would defeat the
  // `theme === null` guard above and stop the app from following OS changes.
  const persistTheme = (isDark: boolean) => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  };

  const toggleTheme = () => {
    if (!isHighContrastMode) {
      const next = !isDarkMode;
      setIsDarkMode(next);
      persistTheme(next);
    }
  };

  const setTheme = (isDark: boolean) => {
    if (!isHighContrastMode) {
      setIsDarkMode(isDark);
      persistTheme(isDark);
    }
  };

  const toggleHighContrast = () => {
    setIsHighContrastMode((prev) => !prev);
  };

  const setHighContrast = (isHighContrast: boolean) => {
    setIsHighContrastMode(isHighContrast);
  };

  const theme = useMemo(() => {
    if (isHighContrastMode) {
      return highContrastTheme;
    }
    return isDarkMode ? darkTheme : lightTheme;
  }, [isDarkMode, isHighContrastMode]);

  const contextValue = useMemo(
    () => ({
      isDarkMode,
      isHighContrastMode,
      toggleTheme,
      setTheme,
      toggleHighContrast,
      setHighContrast,
    }),
    [isDarkMode, isHighContrastMode, setTheme, toggleTheme, toggleHighContrast, setHighContrast]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
